// Single-source the Google/Facebook SSO URL builders that previously lived
// inline in `app/pages/login.vue`. Consumed by the login page and by the
// inline commit-point auth panel (`EventsGuestAuthPanel`).
//
// Each provider URL is built via `buildSsoUrl()` (Laravel `/sso/<provider>/
// redirect` endpoint) then stamped with cross-property attribution params via
// `useCrossPropertyLink()`. Returns `''` passthrough when `buildSsoUrl()`
// returns '' (blank SSO origin — e.g. before runtime config hydrates).

import { computed, toValue } from 'vue';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { buildSsoUrl, type SignupSource } from '~/utils/backend-sign-up-url';

export function useSsoUrls(
  redirectTo: MaybeRefOrGetter<unknown>,
  source: MaybeRefOrGetter<SignupSource | null>,
  placementId: string,
): { googleSsoUrl: ComputedRef<string>; facebookSsoUrl: ComputedRef<string> } {
  const config = useRuntimeConfig();

  // Capture the request origin synchronously during setup — `useRequestURL()`
  // is a request-context composable and must not be called inside a computed.
  // `siteUrl` is set in staging/prod; the origin fallback covers dev/test.
  const frontendOrigin = config.public.siteUrl || useRequestURL().origin;

  function ssoUrl(provider: 'google' | 'facebook'): string {
    // SSO routes live on the Laravel backend (api.* in staging/prod; localhost
    // monolith in dev). `apiOrigin` is empty in dev because /api/* calls go
    // through the Vite proxy, but a top-level nav to /sso/* can't — fall back
    // to `plannerUrl`, which points at the same Laravel backend in dev.
    const raw = buildSsoUrl({
      provider,
      ssoOrigin: config.public.apiOrigin || config.public.plannerUrl,
      frontendOrigin,
      redirectTo: toValue(redirectTo),
      source: toValue(source),
    });
    if (!raw) return '';
    return useCrossPropertyLink({
      url: raw,
      destination: 'planner',
      campaign: 'sso_login',
      placementId,
    });
  }

  const googleSsoUrl = computed(() => ssoUrl('google'));
  const facebookSsoUrl = computed(() => ssoUrl('facebook'));

  return { googleSsoUrl, facebookSsoUrl };
}
